import { createRouter, createWebHashHistory, type RouteLocationNormalized, type RouteLocationRaw } from 'vue-router'
import { useDashboardStore } from '@/stores/dashboard'
import { useAuthStore } from '@/stores/auth'
import { MICROSOFT_ADMIN_PAGE_PATH } from '@lark-automadoc/shared-types'

declare module 'vue-router' {
  interface RouteMeta {
    /**
     * False for a page that must fill the screen on its own (the designer) or
     * belongs to someone with no session (the signing link). Every other page
     * is rendered inside CompanyShell.
     */
    shell?: boolean
    /**
     * The platformFeatures() screen this page is. A session that does not get
     * it (a Microsoft session on Approvals) is sent home rather than shown a
     * page whose every call the server refuses with PLATFORM_MISMATCH.
     */
    feature?: 'approvals' | 'approverSignatures' | 'signatures' | 'powerAutomate'
    /**
     * Readable by someone with no session (App.vue renders it ahead of the sign-in
     * chooser): the IT admin page a customer forwards to their Microsoft 365 admin.
     * Signed in, it is an ordinary page in the shell.
     */
    public?: boolean
  }
}

const router = createRouter({
  history: createWebHashHistory(),
  routes: [
    {
      path: '/',
      name: 'home',
      component: () => import('@/views/Home.vue')
    },
    {
      path: '/contact',
      name: 'contact',
      component: () => import('@/views/Contact.vue')
    },
    {
      // The person's own choice of optional messages (contact center); every member's.
      path: '/notifications',
      name: 'notifications',
      component: () => import('@/views/Notifications.vue')
    },
    {
      // Everyone in the account: role, seat, status (was "Users and seats" and "Members").
      path: '/people',
      name: 'people',
      component: () => import('@/views/People.vue')
    },
    // The two pages People replaced, kept for links and bookmarks.
    { path: '/users', redirect: (to) => ({ path: '/people', query: to.query }) },
    { path: '/members', redirect: (to) => ({ path: '/people', query: to.query }) },
    {
      path: '/templates',
      name: 'templates',
      component: () => import('@/views/Templates.vue')
    },
    {
      // One of the account's own templates (a source or api template): its settings
      path: '/templates/:id',
      name: 'template-detail',
      component: () => import('@/views/TemplateDetail.vue')
    },
    {
      // Documents from one of the account's own templates: a form, or a CSV or Excel file
      path: '/templates/:id/generate',
      name: 'template-generate',
      component: () => import('@/views/TemplateGenerate.vue')
    },
    {
      path: '/approvals',
      name: 'approvals',
      component: () => import('@/views/Approvals.vue'),
      meta: { feature: 'approvals' }
    },
    {
      path: '/approvals/:id',
      name: 'approval-detail',
      component: () => import('@/views/ApprovalDetail.vue'),
      meta: { feature: 'approvals' }
    },
    {
      // Get the apps, and the person's own data connections (every account).
      path: '/integrations',
      name: 'integrations',
      component: () => import('@/views/Connections.vue')
    },
    { path: '/connections', redirect: (to) => ({ path: '/integrations', query: to.query }) },
    {
      // For a customer's IT admin: what AutomaDoc asks of Microsoft 365, and the
      // one-click approval for their organisation. Public: the admin has no account.
      path: MICROSOFT_ADMIN_PAGE_PATH,
      name: 'microsoft-admin',
      component: () => import('@/views/MicrosoftAdmin.vue'),
      meta: { public: true }
    },
    {
      // The Power Automate connector and its recipes (a Microsoft account's automations).
      path: '/power-automate',
      name: 'power-automate',
      component: () => import('@/views/PowerAutomate.vue'),
      meta: { feature: 'powerAutomate' }
    },
    {
      // Template designer. Base templates carry their Base and table in the
      // query (?base=…&table=…), which the Base routes need as headers. An
      // `account` template is one of the account's own: a source template
      // (Excel, SharePoint, Dataverse, Google Sheets) or an api template.
      path: '/design/:source(base|approval|account)/:id',
      name: 'template-design',
      component: () => import('@/views/TemplateDesign.vue'),
      meta: { shell: false }
    },
    {
      path: '/generations',
      name: 'generations',
      component: () => import('@/views/Generations.vue')
    },
    {
      // Template gallery: the use cases, with a starter template for each.
      path: '/gallery',
      name: 'gallery',
      component: () => import('@/views/UseCases.vue')
    },
    { path: '/use-cases', redirect: (to) => ({ path: '/gallery', query: to.query }) },
    // Pricing is the "Compare plans" tab of Billing now.
    { path: '/pricing', redirect: () => ({ path: '/billing', query: { tab: 'plans' } }) },
    {
      path: '/billing',
      name: 'billing',
      component: () => import('@/views/Billing.vue')
    },
    {
      // AutomaDoc Sign (design 90 §7.1): every envelope, and the legacy signature requests
      // beside them. Every platform and every person: members see their own.
      path: '/esign',
      name: 'esign',
      component: () => import('@/views/sign/SignList.vue')
    },
    {
      // One envelope: a draft opens its wizard (or Sign it myself), anything else its
      // detail. Emails and bot cards link here (esignEnvelopeHash, contract 91 item 15).
      path: '/esign/:id',
      name: 'esign-envelope',
      component: () => import('@/views/sign/SignEnvelope.vue'),
      props: true
    },
    {
      // Signature requests from generated documents (Lark), the page Sign replaces where Sign
      // is on (SIGN_AVAILABLE); there, this address goes to #/esign (signRoute below).
      path: '/signatures',
      name: 'signatures',
      component: () => import('@/views/Signatures.vue'),
      meta: { feature: 'signatures' }
    },
    {
      path: '/api-keys',
      name: 'api-keys',
      component: () => import('@/views/ApiKeys.vue')
    },
    // The signing page is rendered by App.vue, not through the router. The
    // route exists so the catch-all below does not rewrite the URL to "/" —
    // which would take the token out of it, and a reload would lose the page.
    {
      path: '/sign/:token',
      name: 'sign',
      component: () => import('@/views/SignDocument.vue'),
      props: true,
      meta: { shell: false }
    },
    // An organisation invitation (#/invite?i=…&t=…). index.html takes it out of the
    // URL before anything loads and the sign-in chooser shows it; this keeps a stray
    // copy (a page served without that inline script) from matching nothing.
    {
      path: '/invite',
      // Not a string: that keeps the query, and with it the token (?t=…), in the URL.
      redirect: () => ({ path: '/', query: {} })
    },
    // An email sign-in link (#/auth/email?c=…&t=…). stores/auth.ts takes the
    // token out of the URL before the router reads it; this only keeps a stray
    // copy from matching nothing.
    {
      path: '/auth/email',
      // Not a string: that keeps the query, and with it the token (?t=…), in the URL.
      redirect: () => ({ path: '/', query: {} })
    },
    // Catch-all redirect to home
    {
      path: '/:pathMatch(.*)*',
      redirect: '/'
    }
  ],
  scrollBehavior(to, _from, savedPosition) {
    if (savedPosition) return savedPosition
    // A section of a page (`/integrations#apps`), once the page has rendered it,
    // clear of the shell's sticky 56px top bar.
    if (to.hash) return new Promise((resolve) => setTimeout(() => resolve({ el: to.hash, top: 72 }), 300))
    return { top: 0 }
  }
})

/**
 * Whether the signed-in session gets the page a route is (meta.feature).
 * Unknown until the tenant has loaded — then true, and CompanyShell re-checks
 * once it knows, so a deep link is never refused on a guess.
 */
export function routeAllowed(to: RouteLocationNormalized): boolean {
  const feature = to.meta.feature
  if (!feature) return true
  const dashboard = useDashboardStore()
  if (!dashboard.loaded || !dashboard.tenant) return true
  const features = dashboard.features
  if (feature === 'powerAutomate') return useAuthStore().platform === 'microsoft'
  return features[feature]
}

const SIGN_ROUTES = new Set<unknown>(['esign', 'esign-envelope', 'signatures'])

/**
 * AutomaDoc Sign or the legacy Signatures page, as the environment says (GET
 * /api/auth/providers `sign`, the API's SIGN_AVAILABLE). Off (or an API too old to say),
 * #/esign goes to Signatures, which the feature check then keeps to the sessions that have
 * it; on, an old #/signatures link goes to #/esign. The API refuses Sign's routes while it is
 * off anyway.
 */
export async function signRoute(to: RouteLocationNormalized): Promise<true | RouteLocationRaw> {
  if (!SIGN_ROUTES.has(to.name)) return true
  const auth = useAuthStore()
  const answer = await auth.loadProviders()
  // No answer at all (a network blip): let the page through rather than lose a deep link;
  // the API's own switch still refuses what is off.
  if (auth.providers === null) return true
  const sign = answer.sign === true
  if (sign && to.name === 'signatures') return { path: '/esign', query: to.query }
  if (!sign && to.name !== 'signatures') return { path: '/signatures' }
  return true
}

// Mirrors the server, never replaces it: the Lark-only routes answer
// PLATFORM_MISMATCH to another platform's session whatever the portal shows.
router.beforeEach(async (to) => {
  const sign = await signRoute(to)
  if (sign !== true) return sign
  return routeAllowed(to) ? true : { path: '/' }
})

export default router
